import clsx from 'clsx'
import { forwardRef, ReactNode, useId, useMemo } from 'react'
import { TSize } from 'src/types'
import { useTabs } from './hooks/useTabs'
import { Tab } from './Tab'
import styles from './Tabs.module.scss'
import { TabsContext, TabsDirection, TabsVariant } from './TabsContext'
import { TabsList } from './TabsList'
import { TabsPanel } from './TabsPanel'

export interface TabsProps {
  direction?: TabsDirection
  variant?: TabsVariant
  size?: TSize
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  children: ReactNode
  id?: string
  className?: string
  fullWidth?: boolean
}

const TabsWrapper = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      direction = 'row',
      size = 'md',
      variant = 'transparent',
      fullWidth = false,
      children,
      value,
      defaultValue,
      id: propId,
      onChange,
      className,
    },
    ref,
  ) => {
    const generatedId = useId()
    const id = propId || generatedId
    const tabs = useTabs({ value, defaultValue, onChange })

    const contextValue = useMemo(
      () => ({ direction, size, variant, wrapperId: id, fullWidth, ...tabs }),
      [tabs, direction, size, variant, fullWidth, id],
    )

    return (
      <TabsContext.Provider value={contextValue}>
        <div ref={ref} className={clsx(styles.tabs, styles[direction], className)}>
          {children}
        </div>
      </TabsContext.Provider>
    )
  },
)

export const Tabs = Object.assign(TabsWrapper, { Tab, Panel: TabsPanel, List: TabsList })
